import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { PlanChangeRequestStatus } from '@neuroedge/shared';
import { Button, Card, PageState, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { useAdminPlanChangeRequests, useReviewPlanChangeRequest } from '../../api/billing';

const TABS: { value: PlanChangeRequestStatus | 'ALL'; label: string }[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'ALL', label: 'All' },
];

function when(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function PlanChangeRequestsPage() {
  const { isPlatformAdmin } = useAuth();
  const [tab, setTab] = useState<PlanChangeRequestStatus | 'ALL'>('PENDING');
  const requests = useAdminPlanChangeRequests(tab === 'ALL' ? undefined : tab);
  const review = useReviewPlanChangeRequest();
  const toast = useToast();

  if (!isPlatformAdmin) return <Navigate to="/" replace />;

  async function decide(requestId: string, decision: 'APPROVED' | 'REJECTED') {
    try {
      await review.mutateAsync({ requestId, input: { decision } });
      toast.success(decision === 'APPROVED' ? 'Plan changed.' : 'Request rejected.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not review this request.');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 960 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Plan Change Requests</h1>

      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        {TABS.map((t) => (
          <Button
            key={t.value}
            type="button"
            variant={tab === t.value ? 'primary' : 'secondary'}
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </Button>
        ))}
      </div>

      <Card>
        {requests.isPending ? (
          <PageState kind="loading" message="Loading…" />
        ) : requests.isError ? (
          <PageState
            kind="error"
            message={requests.error.message}
            onRetry={() => void requests.refetch()}
          />
        ) : requests.data.length === 0 ? (
          <PageState kind="empty" title="Nothing here" />
        ) : (
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 'var(--space-3)',
            }}
          >
            {requests.data.map((req) => (
              <li
                key={req.id}
                style={{
                  borderBottom: '1px solid var(--color-border)',
                  paddingBottom: 'var(--space-3)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 'var(--space-2)',
                  }}
                >
                  <strong>
                    {req.tenantName}{' '}
                    <span style={{ color: 'var(--color-text-muted)' }}>({req.tenantSlug})</span>
                  </strong>
                  <span
                    style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}
                  >
                    {when(req.createdAt)}
                  </span>
                </div>
                <p style={{ margin: 'var(--space-1) 0' }}>
                  Requested <strong>{req.targetPlanName}</strong>{' '}
                  <span className="ne-badge">{req.status}</span>
                </p>
                {req.note ? (
                  <p style={{ margin: 'var(--space-1) 0', color: 'var(--color-text-muted)' }}>
                    “{req.note}”
                  </p>
                ) : null}
                {req.reviewNote ? (
                  <p style={{ margin: 'var(--space-1) 0', color: 'var(--color-text-muted)' }}>
                    Review note: “{req.reviewNote}”
                  </p>
                ) : null}
                {req.status === 'PENDING' ? (
                  <div
                    style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}
                  >
                    <Button
                      disabled={review.isPending}
                      onClick={() => void decide(req.id, 'APPROVED')}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={review.isPending}
                      onClick={() => void decide(req.id, 'REJECTED')}
                    >
                      Reject
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/admin/plans">Pricing plans</Link> · <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
